import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import { IConfiguration } from '../../configuration/config.types';
import { Alert } from '../../modules/alertBar/models/Alert';
import { AlertsUpdatedEvent } from '../../modules/alertBar/models/AlertsUpdatedEvent';
import { ProjectExportFinishedEvent } from '../../modules/projects/details/exportData/events/ProjectExportFinishedEvent';
import { ProjectExportStartedEvent } from '../../modules/projects/details/exportData/events/ProjectExportStartedEvent';
import { ImportAnnotationsFailureResponseEvent } from '../../modules/projects/details/images/importAnnotations/events/ImportAnnotationsFailureResponseEvent';
import { ImportAnnotationsSuccessResponseEvent } from '../../modules/projects/details/images/importAnnotations/events/ImportAnnotationsSuccessResponseEvent';
import { IImportAnnotationsReport } from '../../modules/projects/details/images/importAnnotations/models/ImportAnnotationsReport';
import { cleanClones, IQuestionModel } from '../modules/annotation/question.model';
import { IAnnotationType } from '../modules/annotation/submodules/annotationTypes/models/annotationType';
import { GetAnnotationTypesResponse, IAnnotationTypeResponse } from '../modules/annotation/submodules/annotationTypes/services/getAnnotationTypesService';
import { IAuthStore } from '../modules/auth/auth.store';
import { IProjectDetailsImagesBl } from '../modules/projectDetails/sub/images/projectDetailsImages.bl';
import { IProjectDetailsImagesStore } from '../modules/projectDetails/sub/images/projectDetailsImages.store';
import { ImportStage, IProjectImportReport } from '../modules/projects/projects.model';
import { IProjectsStore } from '../modules/projects/projects.store';
import { BaseHubService } from './baseHub.service';
import { IEventBus } from './eventBus.service';

export interface IProjectHubService {
  initializeAsync(): Promise<void>;
  didQuestionsChangedFor(projectId: string): boolean;
  popNewQuestionsFor(projectId: string): IQuestionModel[];
  didAnnotationTypesChangedFor(projectId: string): boolean;
  popNewAnnotationTypesFor(projectId: string): IAnnotationType[];
}

export namespace IProjectHubService {
  export const $: interfaces.ServiceIdentifier<IProjectHubService> = Symbol('PROJECT_HUB_SERVICE');
}

const HUB_METHODS = {
  QUESTIONS_CHANGED: 'OnProjectQuestionsChanged',
  ANNOTATION_TYPES_CHANGED: 'OnProjectAnnotationTypesChanged',
  DRAFT_IMPORT_FINISHED: 'OnDraftImportFinished',
  DRAFT_IMPORT_FAILED: 'OnDraftImportFailed',
  BATCH_ANSWER_QUESTIONS_FINISHED: 'OnBatchAnswerQuestionsFinished',
  BATCH_ANSWER_QUESTIONS_FAILED: 'OnBatchAnswerQuestionsFailed',
  IMPORT_ANNOTATIONS_FINISHED: 'OnAnnotationsImportFinished',
  IMPORT_ANNOTATIONS_FAILED: 'OnAnnotationsImportFailed',
  ALERTS_UPDATED: 'OnAlertsUpdated',
  PROJECT_EXPORT_FINISHED: 'OnProjectExportFinished',
  PROJECT_EXPORT_STARTED: 'OnProjectExportStarted',
};

@injectable()
export class ProjectHubService extends BaseHubService implements IProjectHubService {
  private newQuestions: IQuestionModel[] = [];
  private newAnnotationTypes: IAnnotationType[] = [];

  constructor(
    @inject(IConfiguration.$) config: IConfiguration,
    @inject(IAuthStore.$) auth: IAuthStore,
    @inject(IProjectDetailsImagesBl.$) private projectDetailsImagesBl: IProjectDetailsImagesBl,
    @inject(IProjectDetailsImagesStore.$) private imagesStore: IProjectDetailsImagesStore,
    @inject(IEventBus.$) private eventBus: IEventBus,
    @inject(IProjectsStore.$) private projectsStore: IProjectsStore
  ) {
    super(auth, config.projectHubUrl);
    makeObservable(this);
    this.addHandlers();
  }

  addHandlers(): void {
    this.connection.on(HUB_METHODS.QUESTIONS_CHANGED, this.handleQuestionsChanged);
    this.connection.on(HUB_METHODS.ANNOTATION_TYPES_CHANGED, this.handleAnnotationTypesChanged);
    this.connection.on(HUB_METHODS.DRAFT_IMPORT_FINISHED, this.handleDraftImportFinished);
    this.connection.on(HUB_METHODS.DRAFT_IMPORT_FAILED, this.handleDraftImportFailed);
    this.connection.on(HUB_METHODS.BATCH_ANSWER_QUESTIONS_FINISHED, this.handleBatchAnswerQuestionsFinished);
    this.connection.on(HUB_METHODS.BATCH_ANSWER_QUESTIONS_FAILED, this.handleBatchAnswerQuestionsFailed);
    this.connection.on(HUB_METHODS.IMPORT_ANNOTATIONS_FINISHED, this.handleImportAnnotationsFinished);
    this.connection.on(HUB_METHODS.IMPORT_ANNOTATIONS_FAILED, this.handleImportAnnotationsFailed);
    this.connection.on(HUB_METHODS.ALERTS_UPDATED, this.handleAlertsUpdated);
    this.connection.on(HUB_METHODS.PROJECT_EXPORT_FINISHED, this.handleProjectExportFinished);
    this.connection.on(HUB_METHODS.PROJECT_EXPORT_STARTED, this.handleProjectExportStarted);
  }

  @action.bound
  handleQuestionsChanged = (projectId: string, response: IQuestionModel[]): void => {
    this.newQuestions = cleanClones(response.filter((q) => q.projectId !== projectId));
  };

  @action.bound
  handleAnnotationTypesChanged = (projectId: string, response: IAnnotationTypeResponse[]): void => {
    // Question is: Should we really get this data here or just ask for them when they are really needed?
    // Using GetAnnotationTypesResponse so the mapping is not copied in two places.
    const apiResponse = new GetAnnotationTypesResponse({ annotationTypes: response });
    this.newAnnotationTypes = this.newAnnotationTypes.filter((q) => q.projectId !== projectId).concat(apiResponse.annotationTypes);
  };

  @action.bound
  handleDraftImportFinished = (report: IProjectImportReport): void => {
    this.projectsStore.importStage = ImportStage.FINISHED;
    this.projectsStore.importReport = report;
  };

  @action.bound
  handleDraftImportFailed = (errorCodes: string[]): void => {
    this.projectsStore.importStage = ImportStage.ERROR;
    this.projectsStore.importErrorCodes?.replace(errorCodes);
  };

  @action.bound
  handleBatchAnswerQuestionsFinished = async (batchNumber: number, totalBatchesCount: number, sessionId: string): Promise<void> => {
    if (this.imagesStore.batchAnswerQuestionModalSessionId !== sessionId) {
      return;
    }

    this.projectDetailsImagesBl.updateBatchAnswerQuestionsProgress(false, totalBatchesCount);
    const { batchAnswerFailedBatches, batchAnswerSuccessfulBatches, batchAnswerBatchesCount } = this.imagesStore;
    if (batchAnswerFailedBatches + batchAnswerSuccessfulBatches === batchAnswerBatchesCount) {
      await this.projectDetailsImagesBl.batchAnswerQuestionsRequestFinishedAsync();
    }
  };

  @action.bound
  handleBatchAnswerQuestionsFailed = async (batchNumber: number, totalBatchesCount: number, sessionId: string): Promise<void> => {
    if (this.imagesStore.batchAnswerQuestionModalSessionId !== sessionId) {
      return;
    }

    this.projectDetailsImagesBl.updateBatchAnswerQuestionsProgress(true, totalBatchesCount);
    const { batchAnswerFailedBatches, batchAnswerSuccessfulBatches, batchAnswerBatchesCount } = this.imagesStore;

    if (batchAnswerFailedBatches + batchAnswerSuccessfulBatches === batchAnswerBatchesCount) {
      await this.projectDetailsImagesBl.batchAnswerQuestionsRequestFinishedAsync();
    }
  };

  handleImportAnnotationsFinished = (report: IImportAnnotationsReport): void => {
    this.eventBus.sendEvent(new ImportAnnotationsSuccessResponseEvent(report));
  };

  handleImportAnnotationsFailed = (errorCodes: string[]): void => {
    this.eventBus.sendEvent(new ImportAnnotationsFailureResponseEvent(errorCodes));
  };

  handleAlertsUpdated = (alerts: Alert[]): void => {
    this.eventBus.sendEvent(new AlertsUpdatedEvent(alerts));
  };

  handleProjectExportStarted = (projectId: string): void => {
    this.eventBus.sendEvent(new ProjectExportStartedEvent(projectId));
  };

  handleProjectExportFinished = (projectId: string): void => {
    this.eventBus.sendEvent(new ProjectExportFinishedEvent(projectId));
  };

  didQuestionsChangedFor(projectId: string): boolean {
    return this.newQuestions.some((q) => q.projectId === projectId);
  }

  didAnnotationTypesChangedFor(projectId: string): boolean {
    return this.newAnnotationTypes.some((q) => q.projectId === projectId);
  }

  popNewQuestionsFor(projectId: string): IQuestionModel[] {
    return cleanClones(this.newQuestions.filter((at) => at.projectId === projectId));
  }

  popNewAnnotationTypesFor(projectId: string): IAnnotationType[] {
    const values = this.newAnnotationTypes.filter((at) => at.projectId === projectId);
    this.newAnnotationTypes = this.newAnnotationTypes.filter((at) => at.projectId !== projectId);
    return values;
  }
}
